import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export function useAttributeGroupsTranslations() {
  const { t } = useTranslation();

  return useMemo(
    () => ({
      // Direct translation functions
      title: String(t('attributeGroups.title')),
      description: String(t('attributeGroups.description')),
      loading: String(t('attributeGroups.loading')),
      noGroups: String(t('attributeGroups.noGroups')),

      // Interpolated functions
      groupsAvailable: (count: number) => String(t('attributeGroups.groupsAvailable', { count })),
      moreAttributes: (count: number) =>
        String(t('attributeGroups.table.moreAttributes', { count })),

      // Table translations
      table: {
        headers: {
          name: String(t('attributeGroups.table.headers.name')),
          order: String(t('attributeGroups.table.headers.order')),
          attributes: String(t('attributeGroups.table.headers.attributes')),
          actions: String(t('attributeGroups.table.headers.actions')),
        },
        noAttributes: String(t('attributeGroups.table.noAttributes')),
        tooltips: {
          editGroup: String(t('attributeGroups.table.tooltips.editGroup')),
          deleteGroup: String(t('attributeGroups.table.tooltips.deleteGroup')),
          readOnlyMode: String(t('attributeGroups.table.tooltips.readOnlyMode')),
        },
      },

      // Actions
      actions: {
        newGroup: String(t('attributeGroups.actions.newGroup')),
        retry: String(t('attributeGroups.actions.retry')),
        cancel: String(t('attributeGroups.actions.cancel')),
        delete: String(t('attributeGroups.actions.delete')),
        save: String(t('attributeGroups.actions.save')),
        saveChanges: String(t('attributeGroups.actions.saveChanges')),
        saving: String(t('attributeGroups.actions.saving')),
        add: String(t('attributeGroups.actions.add')),
        addAttribute: String(t('attributeGroups.actions.addAttribute')),
      },

      // Form translations
      form: {
        createTitle: String(t('attributeGroups.form.createTitle')),
        editTitle: String(t('attributeGroups.form.editTitle')),
        createDescription: String(t('attributeGroups.form.createDescription')),
        editDescription: String(t('attributeGroups.form.editDescription')),
        fields: {
          name: String(t('attributeGroups.form.fields.name')),
          nameRequired: String(t('attributeGroups.form.fields.nameRequired')),
          namePlaceholder: String(t('attributeGroups.form.fields.namePlaceholder')),
          order: String(t('attributeGroups.form.fields.order')),
          orderDescription: String(t('attributeGroups.form.fields.orderDescription')),
          orderDescriptionEdit: String(t('attributeGroups.form.fields.orderDescriptionEdit')),
          attributes: String(t('attributeGroups.form.fields.attributes')),
          attributesInGroup: String(t('attributeGroups.form.fields.attributesInGroup')),
          noAttributesAssigned: String(t('attributeGroups.form.fields.noAttributesAssigned')),
          noAttributesAdded: String(t('attributeGroups.form.fields.noAttributesAdded')),
          availableAttributes: String(t('attributeGroups.form.fields.availableAttributes')),
          selectAttribute: String(t('attributeGroups.form.fields.selectAttribute')),
          selectAttributeDefault: String(t('attributeGroups.form.fields.selectAttributeDefault')),
          noAvailableAttributes: String(t('attributeGroups.form.fields.noAvailableAttributes')),
        },
      },

      // Validation
      validation: {
        nameRequired: String(t('attributeGroups.validation.nameRequired')),
        nameMinLength: String(t('attributeGroups.validation.nameMinLength')),
        nameExists: String(t('attributeGroups.validation.nameExists')),
      },

      // Dialogs
      dialogs: {
        deleteTitle: String(t('attributeGroups.dialogs.deleteTitle')),
        deleteDescription: String(t('attributeGroups.dialogs.deleteDescription')),
        deleteConfirm: String(t('attributeGroups.dialogs.deleteConfirm')),
        deleteCancel: String(t('attributeGroups.dialogs.deleteCancel')),
      },

      // Messages
      messages: {
        loadError: String(t('attributeGroups.messages.loadError')),
        createSuccess: String(t('attributeGroups.messages.createSuccess')),
        updateSuccess: String(t('attributeGroups.messages.updateSuccess')),
        deleteSuccess: String(t('attributeGroups.messages.deleteSuccess')),
        createError: String(t('attributeGroups.messages.createError')),
        updateError: String(t('attributeGroups.messages.updateError')),
        deleteError: String(t('attributeGroups.messages.deleteError')),
      },
    }),
    [t]
  );
}
